import React, { useMemo, useRef, useState } from "react";
import { useRoute, Link } from "wouter";
import {
  ChevronLeft,
  Download,
  UploadCloud,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Banknote,
  FileSpreadsheet,
  Ban,
  X,
} from "lucide-react";
import {
  useGetPaymentRun,
  useExportPaymentRunEft,
  useReconcilePaymentRun,
  useCancelPaymentRun,
  useFailPaymentRun,
  getGetPaymentRunQueryKey,
  getListPaymentRunsQueryKey,
  type ReconcilePaymentRunResult,
  type PaymentRunItem,
} from "@workspace/api-client-react";
import { useUpload } from "@workspace/object-storage-web";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { formatZAR } from "@/lib/formatters";

const ACCEPT = ".xlsx,.xls,.csv";

function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function itemNote(item: PaymentRunItem): string {
  if (item.status === "failed" && item.failureReason) return item.failureReason;
  if (item.status === "held" && item.heldReason) return item.heldReason;
  if (item.status === "paid") {
    return item.bankReference
      ? `Ref ${item.bankReference}`
      : item.paidAt
        ? `Paid ${formatDateTime(item.paidAt)}`
        : "Paid";
  }
  return "—";
}

const OUTCOME_STYLES: Record<string, string> = {
  paid: "bg-green-100 text-[#00A859]",
  failed: "bg-red-100 text-red-700",
  mismatch: "bg-amber-100 text-amber-700",
  duplicate: "bg-amber-100 text-amber-700",
  unmatched: "bg-gray-100 text-gray-600",
};

export function PaymentRunDetail() {
  const [, params] = useRoute("/payment-runs/:id");
  const id = Number(params?.id);
  const queryClient = useQueryClient();
  const { data: run, isLoading, error } = useGetPaymentRun(id);

  const exportRun = useExportPaymentRunEft();
  const reconcileRun = useReconcilePaymentRun();
  const cancelRun = useCancelPaymentRun();
  const failRun = useFailPaymentRun();
  const { uploadFile, isUploading } = useUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [failReason, setFailReason] = useState("");
  const [recon, setRecon] = useState<ReconcilePaymentRunResult | null>(null);
  const [toast, setToast] = useState<{ msg: string; tone: "success" | "error" } | null>(
    null,
  );
  const toastTimer = useRef<number | null>(null);

  function showToast(msg: string, tone: "success" | "error" = "success") {
    setToast({ msg, tone });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4500);
  }

  const totals = useMemo(() => {
    const items = run?.items ?? [];
    return {
      paid: items.filter((i) => i.status === "paid").length,
      failed: items.filter((i) => i.status === "failed").length,
      held: items.filter((i) => i.status === "held").length,
    };
  }, [run]);

  if (Number.isNaN(id)) {
    return <div className="p-8 text-red-600">Invalid payment run.</div>;
  }
  if (isLoading) {
    return (
      <div className="p-8 animate-pulse">
        <div className="h-8 w-1/3 bg-gray-200 rounded mb-6" />
        <div className="h-64 bg-gray-200 rounded" />
      </div>
    );
  }
  if (error || !run) {
    return <div className="p-8 text-red-600">Failed to load payment run.</div>;
  }

  const isDraft = run.status === "draft";
  const isProcessing = run.status === "processing";
  const isCompleted = run.status === "completed";
  const isCancelled = run.status === "cancelled";
  const isFailed = run.status === "failed";
  const isTerminal = isCompleted || isCancelled || isFailed;

  async function handleExport() {
    try {
      const res = await exportRun.mutateAsync({ id });
      const blob = new Blob([res.content], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.fileName;
      a.click();
      URL.revokeObjectURL(url);
      await queryClient.invalidateQueries({ queryKey: getGetPaymentRunQueryKey(id) });
      void queryClient.invalidateQueries({ queryKey: getListPaymentRunsQueryKey() });
      const held = res.itemsHeld ?? 0;
      showToast(
        `EFT file generated — ${res.itemsExported} payment${res.itemsExported === 1 ? "" : "s"} (${formatZAR(res.totalCents)})${held ? `, ${held} held` : ""}.`,
      );
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not generate the EFT file.", "error");
    }
  }

  async function handleReconcileFile(file: File | null) {
    if (!file) return;
    try {
      const uploaded = await uploadFile(file);
      if (!uploaded) throw new Error("File upload failed.");
      const data = await reconcileRun.mutateAsync({
        id,
        data: { objectPath: uploaded.objectPath, fileName: file.name },
      });
      setRecon(data);
      await queryClient.invalidateQueries({ queryKey: getGetPaymentRunQueryKey(id) });
      void queryClient.invalidateQueries({ queryKey: getListPaymentRunsQueryKey() });
      const s = data.summary;
      showToast(`Reconciled — ${s.paid} paid, ${s.failed} failed${s.unmatched ? `, ${s.unmatched} unmatched` : ""}.`);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Reconciliation failed.", "error");
    }
  }

  async function handleCancel() {
    try {
      await cancelRun.mutateAsync({ id });
      await queryClient.invalidateQueries({ queryKey: getGetPaymentRunQueryKey(id) });
      void queryClient.invalidateQueries({ queryKey: getListPaymentRunsQueryKey() });
      showToast("Payment run cancelled. Reserved commissions returned to the pending pool.");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not cancel the run.", "error");
    }
  }

  async function handleFail() {
    try {
      const reason = failReason.trim();
      await failRun.mutateAsync({ id, data: reason ? { reason } : undefined });
      setFailReason("");
      await queryClient.invalidateQueries({ queryKey: getGetPaymentRunQueryKey(id) });
      void queryClient.invalidateQueries({ queryKey: getListPaymentRunsQueryKey() });
      showToast("Payment run marked as failed. Reserved commissions returned to the pending pool.");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Could not mark the run as failed.", "error");
    }
  }

  const busy =
    exportRun.isPending ||
    reconcileRun.isPending ||
    cancelRun.isPending ||
    failRun.isPending ||
    isUploading;

  return (
    <div className="font-['Inter'] text-[#0B132B]">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          handleReconcileFile(e.target.files?.[0] ?? null);
          e.target.value = "";
        }}
      />

      <div className="px-8 py-6 border-b border-gray-200 bg-white">
        <Link
          href="/payment-runs"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#0B132B] mb-3"
        >
          <ChevronLeft size={16} /> Payment Runs
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{run.reference}</h1>
              <StatusBadge status={run.status} />
            </div>
            <p className="text-gray-500 text-sm mt-1">
              Created {formatDateTime(run.createdAt)}
              {run.exportedAt ? ` · Exported ${formatDateTime(run.exportedAt)}` : ""}
              {run.reconciledAt ? ` · Reconciled ${formatDateTime(run.reconciledAt)}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              disabled={busy || isTerminal}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm transition-colors ${
                busy || isTerminal
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-[#E6007E] text-white hover:bg-opacity-90 shadow-sm"
              }`}
            >
              {exportRun.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              {isProcessing ? "Re-download EFT file" : "Generate EFT file"}
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={busy || !isProcessing}
              title={
                isDraft
                  ? "Generate the EFT file first"
                  : isTerminal
                    ? "This run is closed"
                    : undefined
              }
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm border transition-colors ${
                busy || !isProcessing
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-[#0B132B] text-[#0B132B] hover:bg-[#0B132B] hover:text-white"
              }`}
            >
              {isUploading || reconcileRun.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <UploadCloud size={16} />
              )}
              Upload bank result
            </button>
            {!isTerminal && (
              <>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      disabled={busy}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm border transition-colors ${
                        busy
                          ? "border-gray-200 text-gray-400 cursor-not-allowed"
                          : "border-red-300 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600"
                      }`}
                    >
                      <XCircle size={16} />
                      Mark as failed
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Mark run as failed?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This marks the run as failed and returns its reserved
                        commissions to the pending pool so they can be paid in a
                        future run. Already-paid agents are unaffected. This cannot
                        be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <textarea
                      value={failReason}
                      onChange={(e) => setFailReason(e.target.value)}
                      placeholder="Reason (optional) — e.g. bank rejected the batch"
                      rows={3}
                      className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B132B]/20"
                    />
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep run</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleFail}
                        className="bg-red-600 text-white hover:bg-red-700"
                      >
                        Mark as failed
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      disabled={busy}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm border transition-colors ${
                        busy
                          ? "border-gray-200 text-gray-400 cursor-not-allowed"
                          : "border-red-300 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600"
                      }`}
                    >
                      <Ban size={16} />
                      Cancel run
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel this payment run?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This cancels the run and returns its reserved commissions
                        to the pending pool so they can be settled by a future run.
                        Already-paid agents are unaffected. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep run</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleCancel}
                        className="bg-red-600 text-white hover:bg-red-700"
                      >
                        Cancel run
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Once-off fees" value={formatZAR(run.totalOnceOffFeesCents ?? 0)} />
          <StatCard label="Recurring commission" value={formatZAR(run.totalRecurringCommCents ?? 0)} />
          <StatCard label="Total payout" value={formatZAR(run.totalPayoutValueCents ?? 0)} tone="magenta" />
          <StatCard
            label="Agents"
            value={`${run.agentsToPay ?? run.items.length}`}
            sub={`${totals.paid} paid · ${totals.failed} failed · ${totals.held} held`}
          />
        </div>

        {isDraft && (
          <Banner tone="info">
            Generate the bank EFT batch file, process it with your bank, then upload the returned
            result report here to mark each agent paid or failed.
          </Banner>
        )}
        {isProcessing && (
          <Banner tone="info">
            EFT file generated. Once the bank run completes, upload the settlement/result report to
            reconcile this run.
          </Banner>
        )}
        {isCancelled && (
          <Banner tone="info">
            This run was cancelled. Any commissions it had reserved were returned to the pending
            pool and can be settled by a future run.
          </Banner>
        )}
        {isFailed && (
          <Banner tone="info">
            This run was marked as failed. Any commissions it had reserved were returned to the
            pending pool and can be settled by a future run.
          </Banner>
        )}

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Banknote size={18} className="text-[#E6007E]" /> Payout items
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Bank</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Once-off</TableHead>
                  <TableHead className="text-right">Recurring</TableHead>
                  <TableHead className="text-right">Total due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {run.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.agentName}</TableCell>
                    <TableCell>{item.bankName ?? "—"}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {item.accountNumberMasked ?? "—"}
                    </TableCell>
                    <TableCell className="text-right">{formatZAR(item.onceOffCents ?? 0)}</TableCell>
                    <TableCell className="text-right">{formatZAR(item.recurringCents ?? 0)}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatZAR(item.totalDueCents)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={item.status} />
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 max-w-[14rem] truncate" title={itemNote(item)}>
                      {itemNote(item)}
                    </TableCell>
                  </TableRow>
                ))}
                {run.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center p-8 text-gray-500">
                      No payout items in this run.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {recon && <ReconciliationResult result={recon} />}
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="flex items-center gap-3 bg-white border border-gray-200 shadow-lg rounded-lg px-4 py-3 max-w-md">
            {toast.tone === "success" ? (
              <CheckCircle2 size={20} className="text-[#00A859] shrink-0" />
            ) : (
              <XCircle size={20} className="text-red-600 shrink-0" />
            )}
            <span className="text-sm font-medium">{toast.msg}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-gray-400 hover:text-gray-600">
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard(props: { label: string; value: string; sub?: string; tone?: "navy" | "magenta" }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <p className="text-xs text-gray-500 font-medium">{props.label}</p>
      <p
        className={`text-2xl font-bold mt-1 ${
          props.tone === "magenta" ? "text-[#E6007E]" : "text-[#0B132B]"
        }`}
      >
        {props.value}
      </p>
      {props.sub && <p className="text-xs text-gray-500 mt-1">{props.sub}</p>}
    </div>
  );
}

function Banner(props: { tone: "info"; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg px-5 py-4">
      <AlertTriangle size={18} className="text-gray-400 shrink-0 mt-0.5" />
      <p className="text-sm text-gray-600">{props.children}</p>
    </div>
  );
}

function ReconciliationResult({ result }: { result: ReconcilePaymentRunResult }) {
  const { summary, rows } = result;
  const stats: ReadonlyArray<{ label: string; value: string; tone: string }> = [
    { label: "Rows", value: summary.total.toLocaleString("en-ZA"), tone: "navy" },
    { label: "Paid", value: summary.paid.toLocaleString("en-ZA"), tone: "green" },
    { label: "Failed", value: summary.failed.toLocaleString("en-ZA"), tone: "red" },
    { label: "Unmatched", value: summary.unmatched.toLocaleString("en-ZA"), tone: "amber" },
    { label: "Paid value", value: formatZAR(summary.paidValueCents ?? 0), tone: "navy" },
  ];
  const toneMap: Record<string, string> = {
    navy: "text-[#0B132B]",
    green: "text-[#00A859]",
    red: "text-red-600",
    amber: "text-amber-600",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 bg-green-50 border border-green-100 rounded-lg px-5 py-4">
        <FileSpreadsheet size={20} className="text-[#00A859]" />
        <div>
          <p className="font-semibold text-sm">Reconciliation processed</p>
          <p className="text-xs text-gray-600">
            {summary.paid} paid, {summary.failed} failed
            {summary.unresolvedItems
              ? `, ${summary.unresolvedItems} item(s) still awaiting a result`
              : ""}
            .
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 font-medium">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${toneMap[s.tone]}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Result rows</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead>Reference</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Account</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-mono text-xs">{r.reference ?? "—"}</TableCell>
                  <TableCell>{r.agentName ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.accountNumberMasked ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    {r.amountCents != null ? formatZAR(r.amountCents) : "—"}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                        OUTCOME_STYLES[r.outcome] ?? "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {r.outcome}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 max-w-[16rem] truncate" title={r.note ?? ""}>
                    {r.note ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
